import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
} from "lucide-react";

export default function Kontakt() {
  const { t } = useTranslation();

  const emailSubject = encodeURIComponent(t('contact.emailSubject'));
  const emailBody = encodeURIComponent(t('contact.emailBody'));

  const infoItems = t('contact.infoBox.items', { returnObjects: true }) || [];

  return (
    <div data-testid="kontakt-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-white mb-6">
              {t('contact.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('contact.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold font-['Manrope'] text-[#2D3748] mb-6">
                {t('contact.info.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('contact.info.description')}
              </p>

              <div className="space-y-6 mb-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-orange-100 text-orange-500">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2D3748] mb-1">{t('contact.info.serviceArea')}</h3>
                    <p className="text-gray-600">
                      Uelzen und Umgebung (Niedersachsen)
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-orange-100 text-orange-500">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2D3748] mb-1">{t('contact.info.phone')}</h3>
                    <a
                      href="tel:+4915207684235"
                      className="text-gray-600 hover:text-orange-500 transition-colors text-lg"
                      data-testid="contact-phone-link"
                    >
                      +49 152 07684235
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-orange-100 text-orange-500">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2D3748] mb-1">{t('contact.info.email')}</h3>
                    <a
                      href={`mailto:info@mtrenovierung.de?subject=${emailSubject}&body=${emailBody}`}
                      className="text-gray-600 hover:text-orange-500 transition-colors text-lg break-all"
                      data-testid="contact-email-link"
                    >
                      info@mtrenovierung.de
                    </a>
                  </div>
                </div>

                {/* Service Area */}
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-orange-100 text-orange-500">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2D3748] mb-1">{t('contact.info.serviceArea')}</h3>
                    <p className="text-gray-600">
                      {t('contact.info.serviceAreaValue')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Cards */}
            <div className="space-y-6">
              {/* Email Card */}
              <Card className="bg-white border hover:shadow-lg transition-all duration-300" data-testid="contact-email-card">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-orange-500 text-white">
                      <Mail className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748]">
                      {t('contact.emailCard.title')}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {t('contact.emailCard.description')}
                  </p>
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg h-14"
                    data-testid="contact-email-button"
                  >
                    <a href={`mailto:info@mtrenovierung.de?subject=${emailSubject}&body=${emailBody}`}>
                      <Mail className="mr-2 h-5 w-5" />
                      {t('contact.emailCard.button')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Phone Card */}
              <Card className="bg-white border hover:shadow-lg transition-all duration-300" data-testid="contact-phone-card">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-[#2D3748] text-white">
                      <Phone className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748]">
                      {t('contact.phoneCard.title')}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {t('contact.phoneCard.description')}
                  </p>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-[#2D3748] text-[#2D3748] hover:bg-[#2D3748] hover:text-white text-lg h-14"
                    data-testid="contact-call-button"
                  >
                    <a href="tel:+4915207684235">
                      <Phone className="mr-2 h-5 w-5" />
                      {t('contact.phoneCard.button')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Info Box */}
              <Card className="bg-gray-50 border-0">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-[#2D3748] mb-2">
                    {t('contact.infoBox.title')}
                  </h4>
                  <ul className="text-gray-600 space-y-2 text-sm">
                    {Array.isArray(infoItems) && infoItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-orange-500">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map placeholder / Service area info */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-[#2D3748] mb-4">
            {t('contact.serviceAreaSection.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('contact.serviceAreaSection.description')}
          </p>
        </div>
      </section>
    </div>
  );
}
